import React, { useState, useRef } from "react";
import { border, navy, red, surface, muted, danger, card, dark, cream, tan, InjectCSS, CLUB_PASSWORD, ADMIN_PASSWORD } from "./constants";
import { Btn, Input } from "./ui";

export function BatchUploadModal({ onImport, onCancel, existingCount }) {
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = React.useRef();

  function handleFile(file) {
    if (!file || !file.name.endsWith(".csv")) { setError("Please select a CSV file"); return; }
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const parsed = parseCSVStatic(e.target.result);
        if (!parsed || parsed.length === 0) { setError("No valid member data found"); return; }
        setPreview(parsed); setError("");
      } catch (err) { setError("Parse error: " + err.message); }
    };
    reader.readAsText(file, "UTF-8");
  }

  function parseCSVStatic(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return null;
    const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
    const results = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = splitLine(lines[i]);
      if (!vals[0]?.trim()) continue;
      const row = {}; headers.forEach((h, idx) => { row[h] = (vals[idx] || "").trim(); });
      if (!row.name) continue;
      results.push({
        id: Math.random().toString(36).slice(2, 10),
        name: row.name,
        nickname: row.nickname || "",
        nationality: "", bio: "", pin: "", photo: null, cropData: null, cars: [],
      });
    }
    return results;
  }

  function splitLine(line) {
    const result = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (c === ',' && !inQ) { result.push(cur); cur = ""; }
      else cur += c;
    }
    result.push(cur); return result;
  }

  async function doImport() {
    setImporting(true);
    await onImport(preview);
    setImporting(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,14,24,0.82)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10,
        width: "100%", maxWidth: 440, padding: 24, display: "flex", flexDirection: "column", gap: 16,
        animation: "fadeUp 0.18s ease" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: "0.12em", color: navy }}>
            BATCH IMPORT
          </div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer",
            fontSize: 20, color: muted, lineHeight: 1, padding: "0 4px" }}>×</button>
        </div>

        {/* Format guide */}
        <div style={{ background: surface, borderRadius: 6, padding: "10px 14px",
          fontFamily: "'DM Mono', monospace", lineHeight: 1.8 }}>
          <div style={{ color: tan, fontWeight: 700, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>CSV FORMAT</div>
          <div style={{ fontSize: 12, color: navy }}>first_name<span style={{ color: muted }}>,last_name</span></div>
          <div style={{ fontSize: 10, color: muted, marginTop: 6 }}>
            • Row 1 must be a header &nbsp;• <span style={{ color: red }}>name</span> is required<br/>
            • PIN is set by each member on first login
          </div>
        </div>

        {/* Drop zone */}
        {!preview && (
          <div
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${drag ? red : border}`, borderRadius: 8,
              padding: "32px 16px", textAlign: "center", cursor: "pointer",
              background: drag ? `${red}08` : surface, transition: "all 0.15s" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: navy, marginBottom: 4 }}>
              Drop CSV here, or click to select
            </div>
            <div style={{ fontSize: 11, color: muted }}>UTF-8 or Shift-JIS encoding</div>
            <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }}
              onChange={e => handleFile(e.target.files[0])} />
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: `${danger}12`, border: `1px solid ${danger}40`, borderRadius: 6,
            padding: "8px 12px", fontSize: 12, color: danger }}>{error}</div>
        )}

        {/* Preview */}
        {preview && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: navy, marginBottom: 8,
              display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ background: `${tan}22`, color: tan, borderRadius: 4,
                padding: "2px 10px", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{preview.length}</span>
              {preview.length} member{preview.length !== 1 ? "s" : ""} to add (current: {existingCount})
            </div>
            <div style={{ maxHeight: 220, overflow: "auto", border: `1px solid ${border}`,
              borderRadius: 6, fontFamily: "'DM Mono', monospace" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                background: surface, padding: "6px 12px", color: muted,
                borderBottom: `1px solid ${border}`, fontSize: 10, letterSpacing: "0.06em" }}>
                <span>NAME</span><span>NICKNAME</span>
              </div>
              {preview.map((m, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                  padding: "8px 12px", borderBottom: i < preview.length - 1 ? `1px solid ${border}44` : "none",
                  color: navy, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.firstName}
                  </span>
                  <span style={{ color: muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.lastName || "—"}
                  </span>
                </div>
              ))}
            </div>
            <button onClick={() => { setPreview(null); setError(""); }}
              style={{ marginTop: 8, background: "none", border: "none", cursor: "pointer",
                color: muted, fontSize: 11, textDecoration: "underline" }}>
              Choose a different file
            </button>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel}
            style={{ padding: "9px 18px", borderRadius: 6, border: `1px solid ${border}`,
              background: "none", cursor: "pointer", fontSize: 13, color: navy }}>
            Cancel
          </button>
          {preview && (
            <button onClick={doImport} disabled={importing}
              style={{ padding: "9px 22px", borderRadius: 6, border: "none",
                background: importing ? `${red}66` : red, color: "#fff",
                cursor: importing ? "default" : "pointer", fontSize: 13, fontWeight: 700,
                letterSpacing: "0.06em" }}>
              {importing ? "Saving…" : `Import ${preview.length} member${preview.length !== 1 ? "s" : ""}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function LockScreen({ onUnlock, logoSrc }) {
  const [mode, setMode] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const [shaking, setShaking] = useState(false);

  function shake() { setShaking(true); setTimeout(() => setShaking(false), 450); }

  function attempt() {
    if (mode === "member") {
      if (pw === CLUB_PASSWORD || pw === ADMIN_PASSWORD) { onUnlock(pw === ADMIN_PASSWORD); }
      else { setErr(true); shake(); }
    } else if (mode === "admin") {
      if (pw === ADMIN_PASSWORD) { onUnlock(true); }
      else { setErr(true); shake(); }
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: dark,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Mono', monospace", padding: "24px 20px" }}>
      <InjectCSS />
      <img src={logoSrc} alt="TAC Car Club"
        style={{ width: 110, height: 110, borderRadius: "50%", objectFit: "cover",
          border: `3px solid #c8b99a`, marginBottom: 22, boxShadow: `0 4px 20px #c8b99a25` }} />
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32,
        letterSpacing: "0.25em", color: cream, marginBottom: 4, textAlign: "center" }}>
        TAC Car Club
      </div>
      <div style={{ fontSize: 11, color: muted, letterSpacing: "0.4em",
        textTransform: "uppercase", marginBottom: 36, textAlign: "center" }}>
        Members Only
      </div>

      {!mode ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 320 }}>
          <Btn variant="navy" style={{ width: "100%", padding: "16px", fontSize: 15 }}
            onClick={() => setMode("member")}>
            Member Sign In
          </Btn>
          <Btn variant="ghost" style={{ width: "100%", padding: "16px", fontSize: 15 }}
            onClick={() => setMode("admin")}>
            Admin Sign In
          </Btn>
        </div>
      ) : (
        <div className={shaking ? "shake" : ""}
          style={{ background: navy, border: `1px solid #ffffff15`, borderRadius: 8,
            padding: "32px 28px", width: "100%", maxWidth: 320,
            boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18,
              color: mode === "admin" ? red : "#f2ede4", letterSpacing: "0.15em" }}>
              {mode === "admin" ? "Admin Access" : "Member Access"}
            </div>
            <button onClick={() => { setMode(null); setPw(""); setErr(false); }}
              style={{ fontSize: 11, color: "#8892a4", background: "none", border: "none", cursor: "pointer",
                fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em" }}>← Back</button>
          </div>
          {/* Password input */}
          <Input type="password" value={pw} placeholder="••••••••"
            onChange={e => { setPw(e.target.value); setErr(false); }}
            onKeyDown={e => e.key === "Enter" && attempt()}
            autoFocus
            style={{ marginBottom: err ? 10 : 28, borderColor: err ? danger : "#ffffff30",
              fontSize: 20, textAlign: "center", letterSpacing: "0.2em",
              background: "#ffffff18", color: "#f2ede4", caretColor: cream }} />
          {err && <div style={{ fontSize: 12, color: "#ff6b6b", marginBottom: 20,
            fontFamily: "'DM Mono', monospace", textAlign: "center", letterSpacing: "0.08em" }}>
            Incorrect password
          </div>}
          {/* Round START ENGINE button */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button onClick={attempt}
              style={{
                width: 86, height: 86, borderRadius: "50%", flexShrink: 0,
                background: "radial-gradient(circle at 40% 35%, #e84040, #a01818)",
                border: "3px solid #cc2222",
                boxShadow: `0 0 0 2px #1a254088, 0 0 16px ${red}99, inset 0 2px 4px rgba(255,255,255,0.2)`,
                cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                WebkitTapHighlightColor: "transparent", transition: "all 0.12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.06)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
              onMouseDown={e => { e.currentTarget.style.transform = "scale(0.94)"; }}
              onMouseUp={e => { e.currentTarget.style.transform = "scale(1.06)"; }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: "0.15em",
                color: "rgba(255,255,255,0.95)", textAlign: "center", textTransform: "uppercase",
                userSelect: "none", lineHeight: 1 }}>START</span>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: "0.15em",
                color: "rgba(255,255,255,0.95)", textAlign: "center", textTransform: "uppercase",
                userSelect: "none", lineHeight: 1, marginTop: 2 }}>ENGINE</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

